import { DeliveryByWeekday } from "./calendar.js";
import { z } from "zod";
import { Centavos, IsoDateTime, LongText, NonEmptyString, PageQuery, QueryBool, Sex, ShortText, Uuid } from "./common.js";
import { AntibioticPanels } from "./culture.js";

// The catalogue is tenant data (ADR 0009): areas, sample types, containers, methods,
// techniques, equipment, studies with their analytes and reference intervals, packages and
// price lists. Every edit bumps `version`; orders snapshot what they used.

export const Area = z.object({
  id: Uuid,
  name: NonEmptyString,
  sort_order: z.number().int(),
  is_active: z.boolean(),
});
export type Area = z.infer<typeof Area>;

export const SampleType = z.object({ id: Uuid, name: NonEmptyString, is_active: z.boolean() });
export type SampleType = z.infer<typeof SampleType>;

export const Container = z.object({
  id: Uuid,
  name: NonEmptyString,
  /** Cap colour as #RRGGBB — drives the label and the tube pick list. */
  colour_hex: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  colour_name: ShortText.nullable(),
  is_active: z.boolean(),
});
export type Container = z.infer<typeof Container>;

export const Method = z.object({ id: Uuid, name: NonEmptyString, is_active: z.boolean() });
export type Method = z.infer<typeof Method>;

export const Technique = z.object({ id: Uuid, name: NonEmptyString, is_active: z.boolean() });
export type Technique = z.infer<typeof Technique>;

export const Equipment = z.object({
  id: Uuid,
  name: NonEmptyString,
  model: ShortText.nullable(),
  serial_number: ShortText.nullable(),
  branch_id: Uuid.nullable(),
  is_active: z.boolean(),
});
export type Equipment = z.infer<typeof Equipment>;

/**
 * Analyte value kinds. `numeric` and `text` are typed values; `reference` is a text value picked
 * from a closed option list (Negativo / Positivo, Claro / Turbio…); `subtitle` is a layout row
 * inside the report; `document` is an attached file (ultrasound, pathology).
 */
export const AnalyteKind = z.enum(["numeric", "text", "reference", "subtitle", "document"]);
export type AnalyteKind = z.infer<typeof AnalyteKind>;

export const ReferenceInterval = z.object({
  id: Uuid,
  analyte_id: Uuid,
  /** null = a general row; set = this branch's own values, which replace the general rows there (ADR 0020). */
  branch_id: Uuid.nullable().default(null),
  /** null = any sex. */
  sex: Sex.nullable(),
  /** Age band in days so neonates work; null = open-ended. */
  age_min_days: z.number().int().min(0).nullable(),
  age_max_days: z.number().int().min(0).nullable(),
  /** Special condition such as "embarazo"; null = none. */
  condition: ShortText.nullable(),
  low: z.number().nullable(),
  high: z.number().nullable(),
  /** For text/reference analytes: the value that counts as normal. */
  expected_text: ShortText.nullable(),
  /** Printed verbatim on the report; kept even when nothing parsed (migration rule). Vendor
   *  reference tables (age bands, trimesters) run to ~350 characters in the pilot's catalogue,
   *  so this is not a ShortText; the report wraps it. */
  display_text: z.string().trim().max(2000).nullable(),
  sort_order: z.number().int(),
});
export type ReferenceInterval = z.infer<typeof ReferenceInterval>;

/**
 * A branch's own formula for an analyte (migration 0033, ADR 0032): it replaces the general
 * `formula` at that branch; `formula: null` = captured there, not calculated. A branch without an
 * entry inherits the general formula.
 */
export const AnalyteBranchFormula = z.object({ branch_id: Uuid, formula: ShortText.nullable() });
export type AnalyteBranchFormula = z.infer<typeof AnalyteBranchFormula>;

const BranchFormulaList = z
  .array(AnalyteBranchFormula)
  .max(100)
  .refine((xs) => new Set(xs.map((x) => x.branch_id)).size === xs.length, "Una sucursal aparece dos veces en las fórmulas por sucursal.");

export const Analyte = z.object({
  id: Uuid,
  study_id: Uuid,
  code: ShortText,
  name: NonEmptyString,
  kind: AnalyteKind,
  unit: ShortText.nullable(),
  decimals: z.number().int().min(0).max(6).nullable(),
  /** Closed option list for `reference` analytes. */
  options: z.array(ShortText).nullable(),
  /** Tenant-authored arithmetic over sibling analyte codes; evaluated by a sandboxed evaluator. */
  formula: ShortText.nullable(),
  /** Per-branch formulas (ADR 0032). Omitted in a payload = left as they are; read back always. */
  branch_formulas: BranchFormulaList.optional(),
  section: ShortText.nullable(),
  sort_order: z.number().int(),
  is_active: z.boolean(),
  /** A document analyte's report template (sanitised HTML), prefilled in capture; never a result. */
  document_template: z.string().nullable().optional(),
});
export type Analyte = z.infer<typeof Analyte>;

/** `catalogue.document_template.set`: the template of one document analyte; "" removes it. */
export const DocumentTemplateSetPayload = z.object({
  analyte_id: Uuid,
  template: z.string().max(60_000),
});
export type DocumentTemplateSetPayload = z.infer<typeof DocumentTemplateSetPayload>;

export const StudyKind = z.enum(["analytic", "service"]);
export type StudyKind = z.infer<typeof StudyKind>;

export const Study = z.object({
  id: Uuid,
  clave: z.string().trim().min(1).max(20),
  name: NonEmptyString,
  kind: StudyKind,
  area_id: Uuid.nullable(),
  sample_type_id: Uuid.nullable(),
  container_id: Uuid.nullable(),
  method_id: Uuid.nullable(),
  technique_id: Uuid.nullable(),
  equipment_id: Uuid.nullable(),
  /** Patient preparation, printed on the ticket (e.g. "Ayuno de 8 a 12 horas"). */
  indications: LongText.nullable(),
  /** Promised turnaround in days; 0 = same day. Exclusive with `delivery_by_weekday`. */
  delivery_days: z.number().int().min(0).nullable(),
  /** Promised turnaround by the weekday the sample is received (7 offsets, Monday first; ADR 0027). */
  delivery_by_weekday: DeliveryByWeekday.nullable().optional(),
  version: z.number().int().min(1),
  is_active: z.boolean(),
  updated_at: IsoDateTime,
});
export type Study = z.infer<typeof Study>;

/**
 * How a branch runs a study when it differs from the study's own ficha (migration 0015): a NULL
 * column inherits the study's value. Pilot request of 2026-11-02: one QS, "enzimático
 * automatizado" at one branch and "química seca" at the other, instead of two duplicate studies.
 */
export const StudyBranchOverride = z.object({
  branch_id: Uuid,
  method_id: Uuid.nullable(),
  technique_id: Uuid.nullable(),
  equipment_id: Uuid.nullable(),
});
export type StudyBranchOverride = z.infer<typeof StudyBranchOverride>;

export const StudyDetail = Study.extend({
  analytes: z.array(Analyte.extend({ reference_intervals: z.array(ReferenceInterval) })),
  /** Only branches with at least one non-inherited value. */
  branch_overrides: z.array(StudyBranchOverride).default([]),
});
export type StudyDetail = z.infer<typeof StudyDetail>;

export const Package = z.object({
  id: Uuid,
  clave: z.string().trim().min(1).max(20),
  name: NonEmptyString,
  study_ids: z.array(Uuid),
  version: z.number().int().min(1),
  is_active: z.boolean(),
});
export type Package = z.infer<typeof Package>;

export const Company = z.object({
  id: Uuid,
  name: NonEmptyString,
  contact: ShortText.nullable(),
  is_active: z.boolean(),
});
export type Company = z.infer<typeof Company>;

export const PriceList = z.object({
  id: Uuid,
  name: NonEmptyString,
  company_id: Uuid.nullable(),
  is_default: z.boolean(),
  is_active: z.boolean(),
});
export type PriceList = z.infer<typeof PriceList>;

export const Price = z.object({
  id: Uuid,
  price_list_id: Uuid,
  study_id: Uuid.nullable(),
  package_id: Uuid.nullable(),
  amount_cents: Centavos,
  version: z.number().int().min(1),
});
export type Price = z.infer<typeof Price>;

/**
 * One analytic study's specimen as the composer needs it to preview the tubes before saving:
 * the server groups studies into tubes by container and sample type (NULL matches NULL).
 */
export const CatalogueSpecimen = z.object({
  study_clave: z.string(),
  container_id: Uuid.nullable(),
  sample_type_id: Uuid.nullable(),
  container_name: z.string().nullable(),
  container_colour_hex: z.string().nullable(),
  container_colour_name: z.string().nullable(),
  sample_type_name: z.string().nullable(),
});
export type CatalogueSpecimen = z.infer<typeof CatalogueSpecimen>;

/** A study or package as it appears in the order composer: with its price on the active list. */
export const PricedCatalogueItem = z.object({
  kind: z.enum(["study", "package"]),
  id: Uuid,
  clave: z.string(),
  name: z.string(),
  area_name: z.string().nullable(),
  amount_cents: Centavos.nullable(),
  price_id: Uuid.nullable(),
  container_colour_hex: z.string().nullable(),
  indications: z.string().nullable(),
  /** For a package: the claves of its active member studies, in package order. Empty for a study —
   *  and empty for a package whose composition is not defined yet, which cannot be ordered or quoted. */
  study_claves: z.array(z.string()),
  /** The specimens it needs: one for an analytic study, none for a service, one per active analytic member of a package. */
  specimens: z.array(CatalogueSpecimen),
});
export type PricedCatalogueItem = z.infer<typeof PricedCatalogueItem>;

export const CatalogueBrowseQuery = PageQuery.extend({
  q: z.string().trim().max(120).optional(),
  area_id: Uuid.optional(),
  price_list_id: Uuid.optional(),
  /** Without `price_list_id`: price with this branch's list (migration 0018), else the default list. */
  branch_id: Uuid.optional(),
  kind: z.enum(["study", "package", "all"]).default("all"),
  include_inactive: QueryBool.default(false),
});
export type CatalogueBrowseQuery = z.infer<typeof CatalogueBrowseQuery>;

/** `since` delta feed for the client cache (system design §4.1). */
export const SinceQuery = z.object({ since: IsoDateTime.optional() });

/** GET /v1/catalogue — everything the device caches, in one call. */
export const CatalogueSnapshot = z.object({
  areas: z.array(Area),
  sample_types: z.array(SampleType),
  containers: z.array(Container),
  methods: z.array(Method),
  techniques: z.array(Technique),
  equipment: z.array(Equipment),
  studies: z.array(StudyDetail),
  packages: z.array(Package),
  price_lists: z.array(PriceList),
  prices: z.array(Price),
  /** The two antibiotic lists of culture antibiograms, Gram positivos and Gram negativos (ADR 0026). */
  antibiotic_panels: AntibioticPanels.optional(),
  generated_at: IsoDateTime,
});
export type CatalogueSnapshot = z.infer<typeof CatalogueSnapshot>;

// ---- Catalogue editing (Administrador) -----------------------------------------------------

const StudyEditable = Study.omit({ id: true, version: true, updated_at: true, is_active: true });

export const StudyUpsertPayload = StudyEditable.extend({
  study_id: Uuid.nullable().default(null),
  is_active: z.boolean().default(true),
  analytes: z
    .array(
      Analyte.omit({ id: true, study_id: true, is_active: true }).extend({
        analyte_id: Uuid.nullable().default(null),
        reference_intervals: z.array(ReferenceInterval.omit({ id: true, analyte_id: true })).default([]),
      }),
    )
    /** The full analyte list, in report order; omitted = edit the ficha only, analytes untouched.
     *  An existing analyte missing from the list goes inactive (never deleted: results point at it). */
    .optional(),
  /** The full per-branch list; omitted = untouched. A branch missing from the list inherits the
   *  study's values again. Saved in the same study version as the rest of the ficha. */
  branch_overrides: z
    .array(StudyBranchOverride)
    .refine((xs) => new Set(xs.map((x) => x.branch_id)).size === xs.length, "Una sucursal aparece dos veces.")
    .optional(),
});
export type StudyUpsertPayload = z.infer<typeof StudyUpsertPayload>;

export const PackageUpsertPayload = Package.omit({ id: true, version: true }).extend({
  package_id: Uuid.nullable().default(null),
});
export type PackageUpsertPayload = z.infer<typeof PackageUpsertPayload>;

export const PriceSetPayload = z.object({
  price_list_id: Uuid,
  study_id: Uuid.nullable().default(null),
  package_id: Uuid.nullable().default(null),
  amount_cents: Centavos,
});
export type PriceSetPayload = z.infer<typeof PriceSetPayload>;

export const PriceListDuplicatePayload = z.object({
  source_price_list_id: Uuid,
  name: NonEmptyString,
  company_id: Uuid.nullable().default(null),
});
export type PriceListDuplicatePayload = z.infer<typeof PriceListDuplicatePayload>;

const SimpleEntityUpsert = z.object({
  id: Uuid.nullable().default(null),
  name: NonEmptyString,
  is_active: z.boolean().default(true),
});
/**
 * `catalogue.area.upsert`: add, rename or remove («Quitar») a report area. A new area without
 * `sort_order` goes last; an edit without it keeps its place (reordering is
 * `catalogue.area.reorder`). Deactivating an area that active studies still use is refused unless
 * `move_studies_to_area_id` names another active area: those studies move there first.
 */
export const AreaUpsertPayload = SimpleEntityUpsert.extend({
  sort_order: z.number().int().min(0).optional(),
  move_studies_to_area_id: Uuid.nullable().default(null),
});
export type AreaUpsertPayload = z.infer<typeof AreaUpsertPayload>;

/** `catalogue.area.reorder`: every active area, once, in the order the report prints them. */
export const AreaReorderPayload = z.object({
  area_ids: z
    .array(Uuid)
    .min(1)
    .max(500)
    .refine((xs) => new Set(xs).size === xs.length, "Un área aparece dos veces."),
});
export type AreaReorderPayload = z.infer<typeof AreaReorderPayload>;

/**
 * `catalogue.study.reorder_analytes`: the print order of one study's analytes (pilot request of
 * 2026-11-02, "solo mover los antibióticos"). Every active analyte of the study, once, subtitles
 * included: a subtitle moves like any row and the sections follow the new order.
 */
export const StudyReorderAnalytesPayload = z.object({
  study_id: Uuid,
  analyte_ids: z
    .array(Uuid)
    .min(1)
    .max(500)
    .refine((xs) => new Set(xs).size === xs.length, "Un analito aparece dos veces."),
});
export type StudyReorderAnalytesPayload = z.infer<typeof StudyReorderAnalytesPayload>;

/**
 * `catalogue.price_list.update`: rename a list or take it out of use. The default list and a
 * list assigned to a branch cannot be deactivated.
 */
export const PriceListUpdatePayload = z.object({
  price_list_id: Uuid,
  name: NonEmptyString,
  is_active: z.boolean().default(true),
});
export type PriceListUpdatePayload = z.infer<typeof PriceListUpdatePayload>;
export const SampleTypeUpsertPayload = SimpleEntityUpsert;
export const MethodUpsertPayload = SimpleEntityUpsert;
export const TechniqueUpsertPayload = SimpleEntityUpsert;
export const ContainerUpsertPayload = SimpleEntityUpsert.extend({
  colour_hex: Container.shape.colour_hex,
  colour_name: ShortText.nullable().default(null),
});
export const EquipmentUpsertPayload = SimpleEntityUpsert.extend({
  model: ShortText.nullable().default(null),
  serial_number: ShortText.nullable().default(null),
  branch_id: Uuid.nullable().default(null),
});

// ---- Analytes across studies (Catálogo → Analitos) ------------------------------------------

/*
 * An analyte is a row of one study (`analyte.study_id`); the same clave in several studies is
 * several rows that merely share a code — FSH (GINE1) is its own row in each of the 7 studies
 * that report it. Catálogo → Analitos (pilot request of 2026-11-03, "solo modificar los
 * analitos, y no buscarlos por estudio") groups the active rows by clave, and an edit is applied
 * to the studies the Administrador ticks, each getting its own new version (ADR 0009).
 */

/** `GET /v1/catalogue/analytes`: every word of `q` must match the name, clave or unit (accent- and case-insensitive). */
export const AnalyteListQuery = PageQuery.extend({
  q: z.string().trim().max(120).optional(),
  include_subtitles: QueryBool.default(false),
  /** «Mostrar eliminados»: also list the claves whose every row is inactive (flagged `is_deleted`). */
  include_deleted: QueryBool.default(false),
});
export type AnalyteListQuery = z.infer<typeof AnalyteListQuery>;

export const AnalyteStudyRef = z.object({ id: Uuid, clave: z.string(), name: z.string(), is_active: z.boolean() });
export type AnalyteStudyRef = z.infer<typeof AnalyteStudyRef>;

/** One clave of the analyte list: the most common name, unit and kind, and the studies that carry it. */
export const AnalyteGroup = z.object({
  code: z.string(),
  name: z.string(),
  unit: z.string().nullable(),
  kind: AnalyteKind,
  /** Studies with an active analyte of this clave. */
  study_count: z.number().int().min(1),
  /** How many different definitions (name, kind, unit, decimals, options, formula, reference values) the studies hold. */
  variant_count: z.number().int().min(1),
  /** Studies with an active analyte of this clave; for a deleted clave, the studies it was removed from. */
  studies: z.array(AnalyteStudyRef),
  /** True when no study carries the clave any more («Eliminado del catálogo»); only listed with `include_deleted`. */
  is_deleted: z.boolean().default(false),
});
export type AnalyteGroup = z.infer<typeof AnalyteGroup>;

/** `GET /v1/catalogue/analytes/group?code=`: every active row of one clave, with its study. */
export const AnalyteGroupQuery = z.object({ code: z.string().trim().min(1).max(200) });
export type AnalyteGroupQuery = z.infer<typeof AnalyteGroupQuery>;

export const AnalyteGroupRow = z.object({
  study: AnalyteStudyRef.extend({ version: z.number().int() }),
  analyte: Analyte.extend({ reference_intervals: z.array(ReferenceInterval) }),
  /** The study's other active analytes, for checking a formula in that study (ADR 0023). */
  siblings: z.array(
    z.object({ id: Uuid, code: z.string(), kind: AnalyteKind, decimals: z.number().int().nullable(), formula: z.string().nullable(), branch_formulas: z.array(AnalyteBranchFormula).optional() }),
  ),
  /** Orders of this study registered but without any captured value yet: they take the change. */
  open_items: z.number().int().min(0),
});
export type AnalyteGroupRow = z.infer<typeof AnalyteGroupRow>;

export const AnalyteGroupDetail = z.object({ code: z.string(), rows: z.array(AnalyteGroupRow) });
export type AnalyteGroupDetail = z.infer<typeof AnalyteGroupDetail>;

/**
 * `catalogue.analyte.update`: edit one analyte (`analyte_id`) and, explicitly, the analyte of the
 * same clave in each of `also_study_ids`. The clave itself is not edited here (it is the link
 * between the studies; rename it in the study). Every touched study gets a new version.
 */
export const AnalyteUpdatePayload = z.object({
  analyte_id: Uuid,
  also_study_ids: z
    .array(Uuid)
    .max(500)
    .default([])
    .refine((xs) => new Set(xs).size === xs.length, "Un estudio aparece dos veces."),
  name: NonEmptyString,
  kind: z.enum(["numeric", "text", "reference", "document"]),
  unit: ShortText.nullable(),
  decimals: z.number().int().min(0).max(6).nullable(),
  options: z.array(ShortText).max(200).nullable(),
  formula: ShortText.nullable(),
  /** The full per-branch list for every touched study (ADR 0032); omitted = each keeps its own. */
  branch_formulas: BranchFormulaList.optional(),
  reference_intervals: z.array(ReferenceInterval.omit({ id: true, analyte_id: true })).max(100).default([]),
});
export type AnalyteUpdatePayload = z.infer<typeof AnalyteUpdatePayload>;

export const AnalyteUpdateResult = z.object({
  code: z.string(),
  studies: z.array(AnalyteStudyRef.extend({ version: z.number().int() })),
  /** Registered order items without captured values that were re-taken from the catalogue. */
  refreshed_items: z.number().int().min(0),
});
export type AnalyteUpdateResult = z.infer<typeof AnalyteUpdateResult>;

/**
 * `catalogue.study.set_active`: «Quitar del catálogo» / «Volver a activar» (pilot request of
 * 2026-11-03). Never deletes; same rules as the `set-study-active` CLI. A study inside an active
 * package is refused unless `force_packages`.
 */
export const StudySetActivePayload = z.object({
  study_id: Uuid,
  is_active: z.boolean(),
  force_packages: z.boolean().default(false),
});
export type StudySetActivePayload = z.infer<typeof StudySetActivePayload>;

/**
 * `catalogue.analyte.create`: «Nuevo analito» (pilot request of 2026-11-03, "crear un nuevo analito
 * o borrar alguno"). An analyte is a row of a study, so it is born inside the studies named in
 * `study_ids` (at least one), at the end of each list. The clave is unique in the laboratory whatever
 * its capitals or spaces, and a clave that was removed from the catalogue is reactivated, not reused.
 * Each study gets a new version.
 */
export const AnalyteCreatePayload = z.object({
  code: ShortText.refine((c) => c.trim().length > 0, "Escribe la clave."),
  study_ids: z
    .array(Uuid)
    .min(1, "Elige al menos un estudio.")
    .max(500)
    .refine((xs) => new Set(xs).size === xs.length, "Un estudio aparece dos veces."),
  name: NonEmptyString,
  kind: z.enum(["numeric", "text", "reference", "document"]),
  unit: ShortText.nullable(),
  decimals: z.number().int().min(0).max(6).nullable(),
  options: z.array(ShortText).max(200).nullable(),
  formula: ShortText.nullable(),
  /** Per-branch formulas (ADR 0032), the same in every chosen study. */
  branch_formulas: BranchFormulaList.optional(),
  reference_intervals: z.array(ReferenceInterval.omit({ id: true, analyte_id: true })).max(100).default([]),
});
export type AnalyteCreatePayload = z.infer<typeof AnalyteCreatePayload>;

/**
 * `catalogue.analyte.set_active`: «Eliminar del catálogo» (`is_active: false`: every study loses the
 * clave, nothing is deleted) and «Volver a activar» (`is_active: true`: the clave returns, at the end,
 * to the studies in `study_ids` that held it). Refused when a study would be left without analytes.
 */
export const AnalyteSetActivePayload = z
  .object({
    code: ShortText,
    is_active: z.boolean(),
    study_ids: z.array(Uuid).max(500).default([]),
  })
  .refine((p) => !p.is_active || p.study_ids.length > 0, { message: "Elige los estudios donde vuelve el analito.", path: ["study_ids"] });
export type AnalyteSetActivePayload = z.infer<typeof AnalyteSetActivePayload>;
